import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle, Download, Clock, AlertCircle, BookOpen, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BookPurchaseSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("loading");
  const [purchaseData, setPurchaseData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setError("Invalid order");
      return;
    }

    const verifyPurchase = async () => {
      try {
        const res = await axios.get(`${API}/books/purchase/verify?order_id=${orderId}`);
        setPurchaseData(res.data);
        setStatus(res.data.status);
      } catch (err) {
        setStatus("error");
        setError(err.response?.data?.detail || "Verification failed");
      }
    };

    verifyPurchase();
    
    // Poll if pending
    const interval = setInterval(async () => {
      if (status === "pending") {
        try {
          const res = await axios.get(`${API}/books/purchase/verify?order_id=${orderId}`);
          if (res.data.status === "paid") {
            setPurchaseData(res.data);
            setStatus("paid");
            clearInterval(interval);
          }
        } catch {}
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [orderId, status]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-[#111] border border-zinc-800 p-8 text-center"
      >
        {status === "loading" && (
          <>
            <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Verifying Payment</h2>
            <p className="text-zinc-400 font-dm-sans">Please wait while we confirm your purchase...</p>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={32} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Payment Processing</h2>
            <p className="text-zinc-400 font-dm-sans mb-4">
              Your payment is being processed. This page will update automatically.
            </p>
            <p className="text-sm text-zinc-500">Order ID: {orderId}</p>
          </>
        )}

        {status === "paid" && purchaseData && (
          <>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Purchase Successful!</h2>
            <p className="text-zinc-400 font-dm-sans mb-6">
              Thank you for purchasing <strong className="text-white">{purchaseData.book_title}</strong>
            </p>
            
            <div className="bg-zinc-900 border border-zinc-800 p-4 mb-6">
              <p className="text-sm text-zinc-500 mb-2">Your download link has been sent to your email.</p>
              <p className="text-xs text-zinc-600">Link expires in 7 days • Max 5 downloads</p>
            </div>

            {/* Read Online Button */}
            <Link
              to={`/read-book?token=${purchaseData.download_token}`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:opacity-90 transition-opacity mb-3"
            >
              <BookOpen size={18} />
              Read Online
            </Link>

            {/* Download Button - Direct download */}
            <a
              href={`${API}/books/direct-download?token=${purchaseData.download_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-opacity mb-4"
            >
              <Download size={18} />
              Download PDF
            </a>

            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Something Went Wrong</h2>
            <p className="text-zinc-400 font-dm-sans mb-6">{error}</p>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
